/**
 * @license
 * Lodash (Custom Build) <https://lodash.com/>
 * Build: `lodash modularize modern -o ./`
 * Copyright OpenJS Foundation and other contributors <https://openjsf.org/>
 * Released under MIT license <https://lodash.com/license>
 * Based on Underscore.js 1.8.3 <http://underscorejs.org/docs/underscore.html>
 * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
 */

type AnyFunction = (...args: any[]) => any;

interface ThrottleOptions {
  leading?: boolean;
  trailing?: boolean;
}

interface ThrottledFunction<T extends AnyFunction> {
  (...args: Parameters<T>): ReturnType<T> | undefined;
  cancel(): void;
  flush(): ReturnType<T> | undefined;
}

function throttle<T extends AnyFunction>(
  func: T,
  wait: number,
  options: ThrottleOptions = {}
): ThrottledFunction<T> {
  let context: any;
  let args: any[] | null;
  let result: ReturnType<T> | undefined;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastCallTime = 0;

  const { leading = true, trailing = true } = options;

  if (typeof func !== 'function') {
    throw new TypeError('Expected a function');
  }

  wait = +wait || 0;

  function throttled(...throttledArgs: Parameters<T>): ReturnType<T> | undefined {
    const now = Date.now();

    // Set lastCallTime to now if this is the first call and leading is false.
    if (!lastCallTime && !leading) {
      lastCallTime = now;
    }

    // Calculate time since last call.
    const remaining = wait - (now - lastCallTime);
    context = this;
    args = throttledArgs;

    // If remaining time is <= 0 or the system clock has gone backwards.
    if (remaining <= 0 || remaining > wait) {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      lastCallTime = now;
      result = func.apply(context, args);
      // Clear args and context after invocation.
      if (!timeoutId) {
        context = args = null;
      }
    } else if (!timeoutId && trailing) {
      // Set up the trailing call.
      timeoutId = setTimeout(trailingEdge, remaining);
    }

    return result;
  }

  function trailingEdge() {
    const now = Date.now();
    const isLeadingFalse = !leading;

    // Check if there was a call while waiting for the trailing edge.
    const hasBeenCalled = args !== null;

    lastCallTime = isLeadingFalse ? 0 : now;
    timeoutId = null;

    if (hasBeenCalled) {
      result = func.apply(context, args);
    }

    // Clear args and context after trailing invocation.
    if (!timeoutId) {
      context = args = null;
    }

    return result;
  }

  function cancel() {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    lastCallTime = 0;
    timeoutId = context = args = null;
  }

  function flush() {
    if (!timeoutId) {
      return result;
    }
    return trailingEdge();
  }

  throttled.cancel = cancel;
  throttled.flush = flush;

  return throttled;
}

export default throttle
